import { supabase } from "@/lib/supabase";
import { articlesStore } from "@/store/articlesStore";

export interface SectionFlags {
  showPublishDate: boolean;
  showAuthor: boolean;
  showViews: boolean;
  showReadTime: boolean;
  showCategory: boolean;
  showShareCount: boolean;
}

export type ArticleSectionKey =
  | "global"
  | "latestNews"
  | "trendingNews"
  | "hotelsSection"
  | "featuredDestinations"
  | "heroSlider"
  | "categoryPage"
  | "articlePage";

export interface ArticleDisplayFlags {
  global: SectionFlags;
  latestNews: SectionFlags;
  trendingNews: SectionFlags;
  hotelsSection: SectionFlags;
  featuredDestinations: SectionFlags;
  heroSlider: SectionFlags;
  categoryPage: SectionFlags;
  articlePage: SectionFlags;
}

export interface SiteSettings {
  siteName: string;
  siteDesc: string;
  logoUrl: string;
  logoDarkUrl: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  fontScale: number;
  notificationEmail: string;
  articleDisplayFlags: ArticleDisplayFlags;
  sectionBindings: Record<string, string>;
}

const DEFAULT_SECTION_FLAGS: SectionFlags = {
  showPublishDate: true,
  showAuthor: true,
  showViews: true,
  showReadTime: true,
  showCategory: true,
  showShareCount: false,
};

const DEFAULT_ARTICLE_FLAGS: ArticleDisplayFlags = {
  global: { ...DEFAULT_SECTION_FLAGS },
  latestNews: { ...DEFAULT_SECTION_FLAGS },
  trendingNews: { ...DEFAULT_SECTION_FLAGS },
  hotelsSection: { ...DEFAULT_SECTION_FLAGS },
  featuredDestinations: { ...DEFAULT_SECTION_FLAGS },
  heroSlider: { ...DEFAULT_SECTION_FLAGS },
  categoryPage: { ...DEFAULT_SECTION_FLAGS },
  articlePage: { ...DEFAULT_SECTION_FLAGS },
};

const DEFAULT_SECTION_BINDINGS: Record<string, string> = {
  featuredDestinations: "destinations",
  hotelsSection: "hotels",
  latestNews: "",
  trendingNews: "",
  newsCategories: "",
  breakingNews: "",
  opinion: "opinion",
  featuredVideos: "",
  infographics: "",
  gccGuide: "",
  heroSlider: "",
  aviation: "aviation",
  travelTips: "travel-tips",
};

const DEFAULT_SETTINGS: SiteSettings = {
  siteName: "السياحية",
  siteDesc: "الصحيفة السياحية الأولى في الخليج",
  logoUrl:
    "https://static.readdy.ai/image/433f60a06dda16b0e3fd209711ce4024/45c8d4d7d672c9e272b7c78f92025684.png",
  logoDarkUrl: "",
  faviconUrl: "",
  primaryColor: "#D4AF37",
  secondaryColor: "#0A1628",
  fontFamily: "Cairo",
  fontScale: 1.0,
  notificationEmail: "",
  articleDisplayFlags: { ...DEFAULT_ARTICLE_FLAGS },
  sectionBindings: { ...DEFAULT_SECTION_BINDINGS },
};

/* ── Helpers ── */

/** Build SectionFlags from a plain object (with fallbacks to defaults) */
function buildSectionFlags(raw: Record<string, unknown> | undefined): SectionFlags {
  if (!raw) return { ...DEFAULT_SECTION_FLAGS };
  return {
    showPublishDate: (raw.showPublishDate as boolean) ?? DEFAULT_SECTION_FLAGS.showPublishDate,
    showAuthor: (raw.showAuthor as boolean) ?? DEFAULT_SECTION_FLAGS.showAuthor,
    showViews: (raw.showViews as boolean) ?? DEFAULT_SECTION_FLAGS.showViews,
    showReadTime: (raw.showReadTime as boolean) ?? DEFAULT_SECTION_FLAGS.showReadTime,
    showCategory: (raw.showCategory as boolean) ?? DEFAULT_SECTION_FLAGS.showCategory,
    showShareCount: (raw.showShareCount as boolean) ?? DEFAULT_SECTION_FLAGS.showShareCount,
  };
}

/** Migrate old flat article_display_flags to new per-section structure */
function migrateOldFlags(old: Record<string, unknown> | undefined): ArticleDisplayFlags {
  const base = buildSectionFlags(old);
  return {
    global: { ...base },
    latestNews: { ...base },
    trendingNews: { ...base },
    hotelsSection: { ...base },
    featuredDestinations: { ...base },
    heroSlider: { ...base },
    categoryPage: { ...base },
    articlePage: { ...base },
  };
}

/** Get effective flags for a specific section (merges with global defaults) */
export function getSectionFlags(
  flags: ArticleDisplayFlags | undefined,
  section: ArticleSectionKey
): SectionFlags {
  if (!flags) return { ...DEFAULT_SECTION_FLAGS };
  const sectionFlags = flags[section];
  const globalFlags = flags.global;
  if (!sectionFlags) return { ...DEFAULT_SECTION_FLAGS };
  return {
    showPublishDate: sectionFlags.showPublishDate ?? globalFlags.showPublishDate ?? DEFAULT_SECTION_FLAGS.showPublishDate,
    showAuthor: sectionFlags.showAuthor ?? globalFlags.showAuthor ?? DEFAULT_SECTION_FLAGS.showAuthor,
    showViews: sectionFlags.showViews ?? globalFlags.showViews ?? DEFAULT_SECTION_FLAGS.showViews,
    showReadTime: sectionFlags.showReadTime ?? globalFlags.showReadTime ?? DEFAULT_SECTION_FLAGS.showReadTime,
    showCategory: sectionFlags.showCategory ?? globalFlags.showCategory ?? DEFAULT_SECTION_FLAGS.showCategory,
    showShareCount: sectionFlags.showShareCount ?? globalFlags.showShareCount ?? DEFAULT_SECTION_FLAGS.showShareCount,
  };
}

// All supported Arabic-friendly Google Fonts
export const FONT_OPTIONS = [
  { name: "Cairo",        label: "كايرو",          sample: "الصحيفة السياحية",  url: "https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;500;600;700;800;900&display=swap" },
  { name: "Tajawal",      label: "تجوال",          sample: "الصحيفة السياحية",  url: "https://fonts.googleapis.com/css2?family=Tajawal:wght@300;400;500;700;800;900&display=swap" },
  { name: "Almarai",      label: "المراعي",         sample: "الصحيفة السياحية",  url: "https://fonts.googleapis.com/css2?family=Almarai:wght@300;400;700;800&display=swap" },
  { name: "Noto Kufi Arabic", label: "نوتو كوفي",     sample: "الصحيفة السياحية",  url: "https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@300;400;500;600;700;800;900&display=swap" },
  { name: "Readex Pro",   label: "ريدكس برو",      sample: "الصحيفة السياحية",  url: "https://fonts.googleapis.com/css2?family=Readex+Pro:wght@300;400;500;600;700&display=swap" },
  { name: "IBM Plex Sans Arabic", label: "IBM بلكس",  sample: "الصحيفة السياحية",  url: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@300;400;500;600;700&display=swap" },
  { name: "Lateef",       label: "لطيف",           sample: "الصحيفة السياحية",  url: "https://fonts.googleapis.com/css2?family=Lateef:wght@300;400;500;600;700;800&display=swap" },
  { name: "Amiri",        label: "أميري",          sample: "الصحيفة السياحية",  url: "https://fonts.googleapis.com/css2?family=Amiri:ital,wght@0,400;0,700;1,400;1,700&display=swap" },
];

export const FONT_SCALE_OPTIONS = [
  { value: 0.85, label: "صغير جداً",   desc: "85%" },
  { value: 0.92, label: "صغير",       desc: "92%" },
  { value: 1.00, label: "افتراضي",    desc: "100%" },
  { value: 1.08, label: "كبير",       desc: "108%" },
  { value: 1.16, label: "كبير جداً",   desc: "116%" },
];

export function applyThemeFontScale(scale: number) {
  document.documentElement.style.setProperty("--font-scale", String(scale));
  // Apply to html font-size so rem units scale globally
  document.documentElement.style.fontSize = `${scale * 16}px`;
}

export function applyThemeFont(fontName: string) {
  const font = FONT_OPTIONS.find((f) => f.name === fontName);
  if (!font) return;

  // Inject Google Fonts link if not already loaded
  const linkId = `gfont-${fontName.replace(/\s+/g, "-")}`;
  if (!document.getElementById(linkId)) {
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href = font.url;
    document.head.appendChild(link);
  }

  // Apply via CSS variable
  document.documentElement.style.setProperty("--font-family", `'${fontName}', sans-serif`);
}

export function applyThemeColors(primary: string, secondary: string) {
  const root = document.documentElement;
  root.style.setProperty("--color-primary", primary);
  root.style.setProperty("--color-secondary", secondary);

  const hexToRgb = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  };
  try {
    root.style.setProperty("--color-primary-rgb", hexToRgb(primary));
    root.style.setProperty("--color-secondary-rgb", hexToRgb(secondary));
  } catch (_) { /* ignore */ }
}

/* ── Cross-tab sync helpers ── */
function broadcastSettingsUpdate() {
  try {
    const bc = new BroadcastChannel("alsiyahiah-settings");
    bc.postMessage({ type: "settings-updated", timestamp: Date.now() });
    bc.close();
  } catch (_) { /* ignore */ }
  try {
    localStorage.setItem("alsiyahiah-settings-ts", String(Date.now()));
  } catch (_) { /* ignore */ }
}

function mapRow(row: Record<string, unknown>): SiteSettings {
  const rawFlags = row.article_display_flags as Record<string, unknown> | undefined;
  const rawBindings = row.section_bindings as Record<string, unknown> | undefined;

  // Detect old flat structure vs new per-section structure
  const isOldFlat = rawFlags && !rawFlags.global && (
    rawFlags.showPublishDate !== undefined ||
    rawFlags.showAuthor !== undefined ||
    rawFlags.showViews !== undefined
  );

  let flags: ArticleDisplayFlags;
  if (isOldFlat) {
    flags = migrateOldFlags(rawFlags);
  } else if (rawFlags && rawFlags.global) {
    flags = {
      global: buildSectionFlags(rawFlags.global as Record<string, unknown>),
      latestNews: buildSectionFlags(rawFlags.latestNews as Record<string, unknown> | undefined),
      trendingNews: buildSectionFlags(rawFlags.trendingNews as Record<string, unknown> | undefined),
      hotelsSection: buildSectionFlags(rawFlags.hotelsSection as Record<string, unknown> | undefined),
      featuredDestinations: buildSectionFlags(rawFlags.featuredDestinations as Record<string, unknown> | undefined),
      heroSlider: buildSectionFlags(rawFlags.heroSlider as Record<string, unknown> | undefined),
      categoryPage: buildSectionFlags(rawFlags.categoryPage as Record<string, unknown> | undefined),
      articlePage: buildSectionFlags(rawFlags.articlePage as Record<string, unknown> | undefined),
    };
  } else {
    flags = { ...DEFAULT_ARTICLE_FLAGS };
  }

  // Parse section bindings
  let sectionBindings: Record<string, string>;
  if (rawBindings && typeof rawBindings === "object") {
    sectionBindings = { ...DEFAULT_SECTION_BINDINGS };
    for (const [key, val] of Object.entries(rawBindings)) {
      if (typeof val === "string") sectionBindings[key] = val;
    }
  } else {
    sectionBindings = { ...DEFAULT_SECTION_BINDINGS };
  }

  return {
    siteName: (row.site_name as string) || DEFAULT_SETTINGS.siteName,
    siteDesc: (row.site_desc as string) || DEFAULT_SETTINGS.siteDesc,
    logoUrl: (row.logo_url as string) || DEFAULT_SETTINGS.logoUrl,
    logoDarkUrl: (row.logo_dark_url as string) || "",
    faviconUrl: (row.favicon_url as string) || "",
    primaryColor: (row.primary_color as string) || DEFAULT_SETTINGS.primaryColor,
    secondaryColor: (row.secondary_color as string) || DEFAULT_SETTINGS.secondaryColor,
    fontFamily: (row.font_family as string) || DEFAULT_SETTINGS.fontFamily,
    fontScale: typeof row.font_scale === "number" ? (row.font_scale as number) : DEFAULT_SETTINGS.fontScale,
    notificationEmail: (row.notification_email as string) || "",
    articleDisplayFlags: flags,
    sectionBindings,
  };
}

const SETTINGS_CACHE_MS = 5 * 60 * 1000;
let cachedSettings: { value: SiteSettings; timestamp: number } | null = null;
let settingsRequest: Promise<SiteSettings> | null = null;

async function fetchSettings(): Promise<SiteSettings> {
  if (settingsRequest) return settingsRequest;
  settingsRequest = (async () => {
    const { data, error } = await supabase
      .from("site_settings")
      .select("*")
      .eq("id", 1)
      .maybeSingle();
    const value = error || !data ? DEFAULT_SETTINGS : mapRow(data as Record<string, unknown>);
    cachedSettings = { value, timestamp: Date.now() };
    return value;
  })().finally(() => {
    settingsRequest = null;
  });
  return settingsRequest;
}

export const siteSettingsStore = {
  async get(): Promise<SiteSettings> {
    if (cachedSettings && Date.now() - cachedSettings.timestamp < SETTINGS_CACHE_MS) {
      return cachedSettings.value;
    }
    return fetchSettings();
  },

  async getFresh(): Promise<SiteSettings> {
    cachedSettings = null;
    return fetchSettings();
  },

  async update(fields: Partial<SiteSettings>): Promise<SiteSettings> {
    const payload: Record<string, unknown> = {};
    if (fields.siteName !== undefined) payload.siteName = fields.siteName;
    if (fields.siteDesc !== undefined) payload.siteDesc = fields.siteDesc;
    if (fields.logoUrl !== undefined) payload.logoUrl = fields.logoUrl;
    if (fields.logoDarkUrl !== undefined) payload.logoDarkUrl = fields.logoDarkUrl;
    if (fields.faviconUrl !== undefined) payload.faviconUrl = fields.faviconUrl;
    if (fields.primaryColor !== undefined) payload.primaryColor = fields.primaryColor;
    if (fields.secondaryColor !== undefined) payload.secondaryColor = fields.secondaryColor;
    if (fields.fontFamily !== undefined) payload.fontFamily = fields.fontFamily;
    if (fields.fontScale !== undefined) payload.fontScale = fields.fontScale;
    if (fields.notificationEmail !== undefined) payload.notificationEmail = fields.notificationEmail;
    if (fields.articleDisplayFlags !== undefined) payload.articleDisplayFlags = fields.articleDisplayFlags;
    if (fields.sectionBindings !== undefined) payload.sectionBindings = fields.sectionBindings;

    // Approach 1: Try direct Supabase client first (faster, no Edge Function needed)
    try {
      const dbPayload: Record<string, unknown> = { id: 1, updated_at: new Date().toISOString() };
      if (payload.siteName !== undefined) dbPayload.site_name = payload.siteName;
      if (payload.siteDesc !== undefined) dbPayload.site_desc = payload.siteDesc;
      if (payload.logoUrl !== undefined) dbPayload.logo_url = payload.logoUrl;
      if (payload.logoDarkUrl !== undefined) dbPayload.logo_dark_url = payload.logoDarkUrl;
      if (payload.faviconUrl !== undefined) dbPayload.favicon_url = payload.faviconUrl;
      if (payload.primaryColor !== undefined) dbPayload.primary_color = payload.primaryColor;
      if (payload.secondaryColor !== undefined) dbPayload.secondary_color = payload.secondaryColor;
      if (payload.fontFamily !== undefined) dbPayload.font_family = payload.fontFamily;
      if (payload.fontScale !== undefined) dbPayload.font_scale = payload.fontScale;
      if (payload.notificationEmail !== undefined) dbPayload.notification_email = payload.notificationEmail;
      if (payload.articleDisplayFlags !== undefined) dbPayload.article_display_flags = payload.articleDisplayFlags;
      if (payload.sectionBindings !== undefined) dbPayload.section_bindings = payload.sectionBindings;

      const { data, error } = await supabase
        .from("site_settings")
        .upsert(dbPayload, { onConflict: "id" })
        .select()
        .single();

      if (!error && data) {
        broadcastSettingsUpdate();
        window.dispatchEvent(new CustomEvent("alsiyahiah:settings:updated"));
        await articlesStore.reset();
        window.dispatchEvent(new CustomEvent("alsiyahiah:articles:updated"));
        return mapRow(data as Record<string, unknown>);
      }
    } catch (err) {
      // Direct update failed — fall through to Edge Function
      console.warn("Direct site_settings update failed, trying Edge Function:", err);
    }

    // Approach 2: Edge Function fallback
    let resultData: Record<string, unknown> | undefined;

    try {
      const { data: result, error } = await supabase.functions.invoke<{
        success?: boolean;
        error?: string;
        data?: Record<string, unknown>;
      }>("update-site-settings", { body: payload });

      if (error || !result?.success) {
        throw new Error(result?.error || error?.message || "update-site-settings failed");
      }
      resultData = result.data;
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : String(err));
    }

    broadcastSettingsUpdate();
    window.dispatchEvent(new CustomEvent("alsiyahiah:settings:updated"));

    // CRITICAL: invalidate article caches so sections fetch fresh data with new bindings
    await articlesStore.reset();
    window.dispatchEvent(new CustomEvent("alsiyahiah:articles:updated"));

    // Return the updated settings directly if the Edge Function returned them
    if (resultData) {
      return mapRow(resultData);
    }

    // Fallback: fetch fresh
    return this.get();
  },
};
